<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="page-product-list" class="ag-grid-container">
    <div class="vx-card">
      <grid-panel
        height="md"
        :column-defs="columnDefs"
        :url="() => this.commercialProductList"
        :auto-size="true"
        @rowClicked="view"
        class="commercial-products-grid"
        @update:deleted="getCommercialProducts"
        ref="commercialProductsGrid"
        title="Commercial Products IPR Coverholder"
        @rowSelected="onRowSelected"
      >
        <vs-dropdown-item @click="addAllWithSalesCountry" :disabled="addingAllWithSalesCountry">
          <span class="flex items-center">
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Add All Commercial Products with Sales Country</span>
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item @click="showRemovePopup = true" :disabled="removingCommercialProduct">
          <span class="flex items-center">
            <feather-icon icon="MinusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Remove Selected Commercial Product(s)</span>
          </span>
        </vs-dropdown-item>
      </grid-panel>
    </div>

    <vs-popup title="Warning" :active="showRemovePopup" button-close-hidden>
      <p>Are you sure you want to remove the selected Commercial Product(s)</p>
      <div class="vx-col w-full text-right my-3">
        <vs-button class="ml-3" v-if="!removingCommercialProduct" @click="removeCommercialProducts" color="light">Confirm</vs-button>
        <vs-button class="ml-3" @click="showRemovePopup = false">Cancel</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>

import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import CanAccessMixin from "@/components/mixins/CanAccessMixin";

// Cell Renderer
import TGrid from "@/views/components/ag-grid/TGrid";
import CellMatchingMixin from "@/views/pages/sync/CellMatchingMixin";
import GridPanel from "@/views/components/ag-grid/GridPanel";
import NotificationMixin from "@/components/mixins/NotificationMixin";
import IprProductCompanyTypeMixin from "@/components/mixins/IprProductCompanyTypeMixin";

export default {
  components: {
    TGrid,
    GridPanel,
  },
  mixins: [CellMatchingMixin, NotificationMixin, CanAccessMixin, IprProductCompanyTypeMixin],
  props: {
    ProductCompanyType: {
      type: String,
      default: 'coverholder-products',
    },
    ProductCompanyType_FK: {
      type: Number,
      default: 2,
    },
  },
  data () {
    return {
      showRemovePopup: false,
      selectedCommercialProductIds: [],
      commercialProductList: {},
      producersList: null,
      searchQuery: '',
      showDeleted: false,
      addingAllWithSalesCountry: false,
      removingCommercialProduct: false,
      producerGridOptions: {
        // groupUseEntireRow: true,
        // groupRowRendererParams: {suppressCount: true},
      },
      selectedProducer: 0,

      modals: {
        transferModal: {
          visible: false
        },
      },

      // AgGrid
      columnDefs: [
      {
          headerName: '',
          field: 'id',
          width: 50,
          filter: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          valueGetter: (row) => {
            if (parseInt(row.data.CommercialProduct_PK) === parseInt(this.$route.params.id)) {
              row.node.setSelected(true)
            }
          }
        },
        {
          headerName: 'Commercial Product',
          field: 'CommercialProductName',
          filter: 'agTextColumnFilter',
          sort: 'asc',
        },
        {
          headerName: 'Risk Class',
          field: 'RiskCodeName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sub Risk Class',
          field: 'SubRiskCodeName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sales Country',
          field: 'salesCountryType',
          filter: 'agTextColumnFilter',
          minWidth: 170,
        },
      ],
      autoGroupColumnDef: {
        headerName: 'Producer Code',
        minWidth: 325,
        cellRendererParams: {
          checkbox: true,
        },
      },
    }
  },
  methods: {
    onRowSelected(event) {
      this.toggleCommerProductSelection(event);
    },
    toggleCommerProductSelection(params) {
      const isSelected = params.node.isSelected();

      if (isSelected && !this.selectedCommercialProductIds.includes(params.data.id)) {
        this.selectedCommercialProductIds.push(params.data.id);
      } 
      
      if (!isSelected) {
        this.selectedCommercialProductIds = this.selectedCommercialProductIds.filter(id => id !== params.data.id);
      }
    },
    getCommercialProducts(isDeleted = 0) {
      const params = {
        IsDeleted: isDeleted,
        companyId: this.$route.params.id,
      };

      this.commercialProductList = this.$api[this.companyTypeApiModel('commercialProductsIpr')].list({ params });

      return this.commercialProductList;
    },
    view({data}) {
      // Handle grouped rows
      if (!data) {
        return;
      }

      const id = data.CommercialProduct_PK || data.id;

      this.$router.push({
        name: 'commercial-products.view',
        params: {id}
      }).catch(() => {
      });
    },
    modalUpdated(modalName) {
      this.closeModal(modalName);
      this.notifySuccess('The commercial products of the selected Producer were successfully copied')
    },
    openModal(modalName) {
      this.modals[modalName].visible = true;
    },
    closeModal(modalName) {
      this.modals[modalName].visible = false;
    },
    modalError(errorMessage) {
      this.$vs.notify({
        color: 'danger',
        title: 'Error occurred',
        text: errorMessage,
        position: 'top-right'
      })
    },
    async addAllWithSalesCountry() {
      try {
        this.addingAllWithSalesCountry = true;
        const { data } = await this.$api.companies.getSalesCountry(this.$route.params.id);
        
        if (!data.data || !data.data.SalesCountryType_FK) {
          this.notifyError('Sales Country missing, please add missing information');
          return;
        }

        await this.$api.companies.addAllCommercialProductsCoverholderWithSalesCountry(this.$route.params.id);
        
        this.notifySuccess('All Commercial Products added successfully');
      } catch (error) {
        this.notifyError('Failed to add Commercial Products');
        console.error(error);
      } finally {
        this.getCommercialProducts();
        this.$refs.commercialProductsGrid.list();
        this.addingAllWithSalesCountry = false;
      }
    },
    async removeCommercialProducts() {
      this.showRemovePopup = false;

      if (this.readOnly) {
        this.notifyError('You do not have permission to do this');
        return false;
      }

      if (!this.selectedCommercialProductIds.length) {
        this.notifyError('Please select at least one Commercial Product');

        return false;
      }

      const producerTxt = this.selectedCommercialProductIds.length > 1 ? 'Commercial Products' : 'Commercial Product';

      try {
        this.removingCommercialProduct = true;
        this.selectedCommercialProductIds = [...new Set(this.selectedCommercialProductIds)];

        const { data } = await this.$api.companies.removeCommercialProductsCoverholder(this.selectedCommercialProductIds, this.$route.params.id);
        this.notifySuccess(`${producerTxt} removed`);
      } catch (e) {
        this.notifyError(`Failed to remove ${producerTxt}`);
        console.error(e)
      } finally {
        this.getCommercialProducts();
        this.$refs.commercialProductsGrid.list();
        this.removingCommercialProduct = false;
      }
    },
  },
  created() {
    this.getCommercialProducts();
  },
}

</script>
